/**
 * API Service
 *
 * HTTP client for GridTwinX REST API.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

const normalizeApiBase = (root?: string) => {
  const trimmed = root ? root.replace(/\/+$/, '') : '';
  if (!trimmed) return '/api/v1';
  return trimmed.endsWith('/api/v1') ? trimmed : `${trimmed}/api/v1`;
};

const resolveApiRoot = () => {
  const fromEnv = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (fromEnv) {
    if (/^https?:\/\//i.test(fromEnv) || fromEnv.startsWith('/')) return fromEnv;
    return `https://${fromEnv}`;
  }

  if (typeof window === 'undefined') return undefined;

  const hostname = window.location.hostname || '';
  if (!hostname) return undefined;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return undefined;
  if (hostname.endsWith('.pages.dev')) return undefined;

  const baseDomain = hostname.startsWith('www.') ? hostname.slice(4) : hostname;
  return `https://api.${baseDomain}`;
};

export const API_BASE_URL = normalizeApiBase(resolveApiRoot());

export const buildApiUrl = (path: string) => {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

export const apiFetch = (path: string, init?: RequestInit) =>
  fetch(buildApiUrl(path), init);

const normalizeWitsPrices = (items: any[]) => {
  if (!Array.isArray(items)) return [];
  const now = new Date().toISOString();
  return items
    .map((item) => {
      if (item?.node_code && typeof item?.price_nzd_mwh === 'number') {
        return {
          node_code: item.node_code,
          node_name: item.node_name || item.node_code,
          price_nzd_mwh: item.price_nzd_mwh,
          timestamp: item.timestamp || now,
          trading_period: item.trading_period ?? 0,
          node_type: item.node_type,
        };
      }
      if (item?.node && (typeof item?.price === 'number' || typeof item?.value === 'number')) {
        return {
          node_code: item.node,
          node_name: item.node,
          price_nzd_mwh: item.price ?? item.value,
          timestamp: now,
          trading_period: 0,
          node_type: undefined,
        };
      }
      return null;
    })
    .filter(Boolean);
};

// Types
export interface WitsNodalPrice {
  node_code: string;
  node_name: string;
  price_nzd_mwh: number;
  timestamp: string;
  trading_period: number;
  node_type?: string;
}

export interface WitsPricesResponse {
  prices: WitsNodalPrice[];
  timestamp: string;
  trading_period: number;
  source: string;
  provenance: Provenance;
}

export interface WitsPriceHistoryPoint {
  timestamp: string;
  trading_period: number;
  price_nzd_mwh: number;
}

export interface WitsPriceHistoryResponse {
  node_code: string;
  node_name: string;
  start: string;
  end: string;
  prices: WitsPriceHistoryPoint[];
  provenance: Provenance;
}

export interface Case {
  id: string;
  name: string;
  description?: string;
  bus_count: number;
  branch_count: number;
  generator_count: number;
  load_count: number;
  created_at: string;
  updated_at: string;
}

export interface SolveRequest {
  solver_type: 'dc_pf' | 'ac_pf' | 'dc_opf';
  tolerance?: number;
  max_iterations?: number;
  enforce_q_limits?: boolean;
}

export interface TaskResponse {
  task_id: string;
  status: string;
  created_at: string;
  poll_url: string;
  websocket_url: string;
}

export interface RunResult {
  run_id: string;
  case_id: string;
  solver_type: string;
  converged: boolean;
  iterations: number;
  max_mismatch: number;
  solve_time_ms: number;
  created_at: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    cursor?: string;
    has_more: boolean;
  };
}

// API Client
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Handle unauthorized
          console.error('Unauthorized request');
        }
        return Promise.reject(error);
      }
    );
  }

  // Health
  async checkHealth(): Promise<{ status: string }> {
    const response = await this.client.get('/health/live');
    return response.data;
  }

  // Cases
  async listCases(cursor?: string, limit = 20): Promise<PaginatedResponse<Case>> {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    params.set('limit', limit.toString());

    const response = await this.client.get(`/cases?${params}`);
    return response.data;
  }

  async getCase(caseId: string): Promise<Case> {
    const response = await this.client.get(`/cases/${caseId}`);
    return response.data;
  }

  async createCase(caseData: any): Promise<Case> {
    const payload = caseData?.case ? caseData : { case: caseData };
    const response = await this.client.post('/cases', payload);
    return response.data;
  }

  async updateCase(caseId: string, data: Partial<Case>): Promise<Case> {
    const response = await this.client.patch(`/cases/${caseId}`, data);
    return response.data;
  }

  async deleteCase(caseId: string): Promise<void> {
    await this.client.delete(`/cases/${caseId}`);
  }

  // Solver
  async solveAsync(caseId: string, request: SolveRequest): Promise<TaskResponse> {
    const response = await this.client.post(`/cases/${caseId}/solve`, request);
    return response.data;
  }

  async solveSync(caseId: string, request: SolveRequest): Promise<RunResult> {
    const response = await this.client.post(`/cases/${caseId}/solve-sync`, request);
    return response.data;
  }

  // Tasks
  async listTasks(): Promise<any> {
    const response = await this.client.get('/tasks');
    return response.data;
  }

  async getTaskStatus(taskId: string): Promise<any> {
    const response = await this.client.get(`/tasks/${taskId}`);
    return response.data;
  }

  async getTaskResults(taskId: string): Promise<any> {
    const response = await this.client.get(`/tasks/${taskId}/results`);
    return response.data;
  }

  async cancelTask(taskId: string): Promise<any> {
    const response = await this.client.post(`/tasks/${taskId}/cancel`);
    return response.data;
  }

  // Runs
  async listRuns(caseId?: string, limit = 20): Promise<PaginatedResponse<any>> {
    const params = new URLSearchParams();
    if (caseId) params.set('case_id', caseId);
    params.set('limit', limit.toString());

    const response = await this.client.get(`/runs?${params}`);
    return response.data;
  }

  async getRun(runId: string): Promise<any> {
    const response = await this.client.get(`/runs/${runId}`);
    return response.data;
  }

  async verifyRun(runId: string): Promise<any> {
    const response = await this.client.post(`/runs/${runId}/verify`);
    return response.data;
  }

  // Analysis
  async computePtdf(caseId: string, request?: any): Promise<any> {
    const response = await this.client.post(`/cases/${caseId}/ptdf`, request || {});
    return response.data;
  }

  async computeLodf(caseId: string, request?: any): Promise<any> {
    const response = await this.client.post(`/cases/${caseId}/lodf`, request || {});
    return response.data;
  }

  // Chronological
  async runChrono(caseId: string, request: any): Promise<TaskResponse> {
    const response = await this.client.post(`/cases/${caseId}/chrono`, request);
    return response.data;
  }

  // WITS Live Data
  async getWitsPrices(nodeCodes?: string[]): Promise<WitsPricesResponse> {
    const params = new URLSearchParams();
    if (nodeCodes && nodeCodes.length > 0) {
      params.set('node_codes', nodeCodes.join(','));
    }
    const query = params.toString();
    const url = query ? `/wits/prices?${query}` : '/wits/prices';
    const response = await this.client.get(url);
    const data = response.data;
    const now = new Date().toISOString();

    if (Array.isArray(data?.prices)) {
      return {
        ...data,
        prices: normalizeWitsPrices(data.prices) as WitsNodalPrice[],
      };
    }

    // Backward/defensive compatibility (should not happen in normal operation).
    if (Array.isArray(data)) {
      const prices = normalizeWitsPrices(data) as WitsNodalPrice[];
      return {
        prices,
        timestamp: now,
        trading_period: prices[0]?.trading_period ?? 0,
        source: 'WITS',
        provenance: {
          tier: 'BACKUP',
          source: 'LEGACY_WITS_RESPONSE',
          as_of: now,
          generated_at: now,
          inputs: { node_codes: nodeCodes || [] },
          warnings: ['Unexpected legacy response shape from /wits/prices'],
        },
      };
    }

    return {
      prices: [],
      timestamp: now,
      trading_period: 0,
      source: 'WITS',
      provenance: {
        tier: 'BACKUP',
        source: 'WITS',
        as_of: now,
        generated_at: now,
        inputs: { node_codes: nodeCodes || [] },
        warnings: ['Unexpected response shape from /wits/prices'],
      },
    };
  }

  async getWitsPriceHistory(nodeCode: string, start: string, end: string): Promise<WitsPriceHistoryResponse> {
    const params = new URLSearchParams();
    params.set('node_code', nodeCode);
    params.set('start', start);
    params.set('end', end);
    const response = await this.client.get(`/wits/prices/history?${params}`);
    return response.data;
  }

  async getGeneration(): Promise<any> {
    const response = await this.client.get('/wits/generation');
    return response.data;
  }

  // Transpower
  async getHvdcStatus(): Promise<any> {
    const response = await this.client.get('/transpower/hvdc');
    return response.data;
  }

  async getOutages(): Promise<any> {
    const response = await this.client.get('/transpower/outages');
    return response.data;
  }

  // TerriaJS Catalogue
  async getCatalogue(): Promise<any> {
    const response = await this.client.get('/terria-catalog.json');
    return response.data;
  }
}

// =============================================================================
// Wrapper object matching hook expectations
// =============================================================================

const mapTask = (task: any) => ({
  id: task?.task_id || task?.id || '',
  type: task?.task_type || 'unknown',
  status: task?.status,
  progress: task?.progress,
  message: task?.error,
  created_at: task?.created_at,
  updated_at: task?.completed_at || task?.started_at || task?.created_at,
});

export const api = {
  // Health
  getHealth: async () => {
    const client = new ApiClient();
    return client.checkHealth();
  },

  // Cases
  getCases: async () => {
    const client = new ApiClient();
    const response = await client.listCases();
    return response.data;
  },

  getCase: async (caseId: string) => {
    const client = new ApiClient();
    return client.getCase(caseId);
  },

  createCase: async (data: Partial<Case>) => {
    const client = new ApiClient();
    return client.createCase(data);
  },

  updateCase: async (caseId: string, data: Partial<Case>) => {
    const client = new ApiClient();
    return client.updateCase(caseId, data);
  },

  deleteCase: async (caseId: string) => {
    const client = new ApiClient();
    return client.deleteCase(caseId);
  },

  // Solver
  solveCase: async (caseId: string, request: SolveRequest) => {
    const client = new ApiClient();
    return client.solveAsync(caseId, request);
  },

  solveCaseSync: async (caseId: string, request: SolveRequest) => {
    const client = new ApiClient();
    return client.solveSync(caseId, request);
  },

  // Runs
  getRuns: async () => {
    const client = new ApiClient();
    const response = await client.listRuns();
    return response.data;
  },

  getCaseRuns: async (caseId: string) => {
    const client = new ApiClient();
    const response = await client.listRuns(caseId);
    return response.data;
  },

  getRun: async (runId: string) => {
    const client = new ApiClient();
    return client.getRun(runId);
  },

  getRunResults: async (runId: string) => {
    const client = new ApiClient();
    return client.getRun(runId); // Results embedded in run
  },

  verifyRun: async (runId: string) => {
    const client = new ApiClient();
    return client.verifyRun(runId);
  },

  getRunAttribution: async (runId: string): Promise<AttributionResponse> => {
    const response = await apiFetch(`/runs/${runId}/attrib`);
    if (!response.ok) throw new Error('Failed to fetch run attribution');
    return response.json();
  },

  // Tasks
  getTasks: async () => {
    const client = new ApiClient();
    const tasks = await client.listTasks();
    return Array.isArray(tasks) ? tasks.map(mapTask) : [];
  },

  getTask: async (taskId: string) => {
    const client = new ApiClient();
    const task = await client.getTaskStatus(taskId);
    return mapTask(task);
  },

  cancelTask: async (taskId: string) => {
    const client = new ApiClient();
    return client.cancelTask(taskId);
  },

  // WITS Live Data
  getWitsPrices: async (nodeCodes?: string[]) => {
    const client = new ApiClient();
    return client.getWitsPrices(nodeCodes);
  },

  getWitsPriceHistory: async (nodeCode: string, hours: number) => {
    const client = new ApiClient();
    const end = new Date();
    const start = new Date(end.getTime() - hours * 60 * 60 * 1000);
    return client.getWitsPriceHistory(nodeCode, start.toISOString(), end.toISOString());
  },

  getWitsGeneration: async () => {
    const client = new ApiClient();
    return client.getGeneration();
  },

  // Transpower
  getTranspowerHvdc: async () => {
    const client = new ApiClient();
    return client.getHvdcStatus();
  },

  getTranspowerOutages: async () => {
    const client = new ApiClient();
    return client.getOutages();
  },

  // Catalogue
  getCatalogue: async () => {
    const client = new ApiClient();
    return client.getCatalogue();
  },
};

// =============================================================================
// NZ Grid API
// =============================================================================

export interface BranchSummary {
  id: string;
  code: string;
  from_bus: string;
  to_bus: string;
  rate_mva: number;
}

export interface GeneratorSummary {
  id: string;
  code: string;
  name: string;
  capacity_mw: number;
  fuel_type: string;
}

export interface NZGridSummary {
  total_substations: number;
  total_generators: number;
  total_capacity_mw: number;
  north_island_capacity_mw: number;
  south_island_capacity_mw: number;
  hvdc_capacity_mw: number;
  generation_by_type: Record<string, number>;
  branches: BranchSummary[];
  generators: GeneratorSummary[];
}

export interface HvdcLinkResult {
  id: string;
  from_bus: string;
  to_bus: string;
  capacity_mw: number;
  flow_mw: number | null;
  utilization_percent: number | null;
}

export interface LmpSummary {
  available: boolean;
  method: 'approximate' | 'exact' | 'unavailable';
  min_lmp: number | null;
  max_lmp: number | null;
  avg_lmp: number | null;
  reason: string | null;
}

export interface NZSolverResult {
  converged: boolean;
  iterations: number;
  max_mismatch: number;
  solve_time_ms: number;
  provenance: Provenance;
  bus_results: Array<{
    bus_id: number;
    name: string;
    code: string;
    vm_pu: number;
    va_deg: number;
    p_mw: number;
    q_mvar: number;
    voltage_status: 'normal' | 'warning' | 'critical';
    lmp: number | null;
  }>;
  branch_results: Array<{
    branch_id: number;
    from_bus: number;
    to_bus: number;
    p_mw: number;
    loading_percent: number;
  }>;
  hvdc_links: HvdcLinkResult[];
  lmp_summary: LmpSummary;
  summary: {
    total_generation_mw: number;
    total_load_mw: number;
    total_losses_mw: number;
    min_voltage_pu: number;
    max_voltage_pu: number;
  };
}

export const nzGridApi = {
  // Get grid summary
  getSummary: async (): Promise<NZGridSummary> => {
    const response = await apiFetch('/nz-grid/summary');
    if (!response.ok) throw new Error('Failed to fetch grid summary');
    return response.json();
  },

  // Get substations
  getSubstations: async (island?: string): Promise<any[]> => {
    const params = island ? `?island=${island}` : '';
    const response = await apiFetch(`/nz-grid/substations${params}`);
    if (!response.ok) throw new Error('Failed to fetch substations');
    return response.json();
  },

  // Get generators
  getGenerators: async (fuelType?: string, island?: string): Promise<any[]> => {
    const params = new URLSearchParams();
    if (fuelType) params.append('fuel_type', fuelType);
    if (island) params.append('island', island);
    const queryString = params.toString();
    const response = await apiFetch(`/nz-grid/generators${queryString ? '?' + queryString : ''}`);
    if (!response.ok) throw new Error('Failed to fetch generators');
    return response.json();
  },

  // Get GeoJSON for map
  getGeoJSON: async (island?: string, loadFactor?: number): Promise<any> => {
    const params = new URLSearchParams();
    if (island) params.append('island', island);
    if (loadFactor !== undefined) params.append('load_factor', loadFactor.toString());
    const queryString = params.toString();
    const url = queryString ? `/nz-grid/geojson?${queryString}` : '/nz-grid/geojson';
    const response = await apiFetch(url);
    if (!response.ok) throw new Error('Failed to fetch GeoJSON');
    return response.json();
  },

  // Get power flow case
  getCase: async (island?: string, loadFactor?: number): Promise<any> => {
    const params = new URLSearchParams();
    if (island) params.append('island', island);
    if (loadFactor) params.append('load_factor', loadFactor.toString());
    const queryString = params.toString();
    const response = await apiFetch(`/nz-grid/case${queryString ? '?' + queryString : ''}`);
    if (!response.ok) throw new Error('Failed to fetch case');
    return response.json();
  },

  // Solve NZ grid power flow
  solve: async (config: {
    solver_type: string;
    tolerance: number;
    max_iterations: number;
    enforce_q_limits: boolean;
    island?: string;
    load_factor?: number;
  }): Promise<NZSolverResult> => {
    const response = await apiFetch('/nz-grid/solve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Solver failed' }));
      throw new Error(error.detail || 'Solver failed');
    }
    return response.json();
  },

  // Get HVDC info
  getHVDC: async (): Promise<any> => {
    const response = await apiFetch('/nz-grid/hvdc');
    if (!response.ok) throw new Error('Failed to fetch HVDC info');
    return response.json();
  },

  // WITS prices
  getWITSPrices: async (): Promise<any> => {
    try {
      const response = await apiFetch('/nz-grid/wits/prices');
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // WITS energy
  getWITSEnergy: async (): Promise<any> => {
    try {
      const response = await apiFetch('/nz-grid/wits/energy');
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // WITS schedules (list available schedule types)
  getWITSSchedules: async (): Promise<any> => {
    try {
      const response = await apiFetch('/nz-grid/wits/schedules');
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // WITS reserves (reserve quantities)
  getWITSReserves: async (
    schedule: string = 'PRS',
    runClass: string = 'InstantaneousReserve',
    island?: 'NI' | 'SI'
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.set('schedule', schedule);
      params.set('run_class', runClass);
      if (island) params.set('island', island);
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/wits/reserves?${query}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // WITS rolling prices (sliding window relative to current trading period)
  getWITSPricesRolling: async (
    schedule: string = 'RTD',
    back: number = 6,
    forward: number = 6,
    island?: 'NI' | 'SI',
    market: 'E' | 'R' = 'E',
    nodes?: string[]
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.set('schedule', schedule);
      params.set('back', back.toString());
      params.set('forward', forward.toString());
      params.set('market', market);
      if (island) params.set('island', island);
      if (nodes && nodes.length > 0) params.set('nodes', nodes.join(','));
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/wits/prices/rolling?${query}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // EM6 Live Data APIs

  // Get current carbon intensity (FREE - no auth required)
  getCarbonIntensity: async (): Promise<CarbonIntensityResponse | null> => {
    try {
      const response = await apiFetch('/nz-grid/em6/carbon-intensity');
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get wind/solar forecast (FREE - no auth required)
  getWindSolarForecast: async (
    generationType?: 'WIN' | 'SOL',
    region?: 'NI' | 'SI' | 'NZ'
  ): Promise<WindSolarForecastResponse | null> => {
    try {
      const params = new URLSearchParams();
      if (generationType) params.set('generation_type', generationType);
      if (region) params.set('region', region);
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/em6/wind-solar-forecast${query ? `?${query}` : ''}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get regional prices (FREE - no auth required)
  getRegionalPrices: async (): Promise<RegionalPricesResponse | null> => {
    try {
      const response = await apiFetch('/nz-grid/em6/regional-prices');
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get grid zone prices (FREE - no auth required)
  getGridZonePrices: async (gridZoneId?: number): Promise<RegionalPricesResponse | null> => {
    try {
      const query = gridZoneId ? `?grid_zone_id=${gridZoneId}` : '';
      const response = await apiFetch(`/nz-grid/em6/grid-zone-prices${query}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get 24-hour nodal price history (FREE - no auth required)
  getNodalPrices24hrs: async (nodeIds?: string): Promise<NodalPrices24HrsResponse | null> => {
    try {
      const query = nodeIds ? `?node_ids=${nodeIds}` : '';
      const response = await apiFetch(`/nz-grid/em6/nodal-prices-24hrs${query}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Transpower ArcGIS APIs (FREE - no auth required)

  // Get transmission lines data
  getTransmissionLines: async (
    voltageKv?: number,
    lineType?: 'TRANSLINE' | 'CABLE',
    maxRecords?: number
  ): Promise<TransmissionLinesResponse | null> => {
    try {
      const params = new URLSearchParams();
      if (voltageKv) params.set('voltage_kv', voltageKv.toString());
      if (lineType) params.set('line_type', lineType);
      if (maxRecords) params.set('max_records', maxRecords.toString());
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/transpower/transmission-lines${query ? `?${query}` : ''}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get transmission lines as GeoJSON for map visualization
  getTransmissionLinesGeoJSON: async (
    voltageKv?: number,
    lineType?: 'TRANSLINE' | 'CABLE',
    maxRecords?: number
  ): Promise<TransmissionLinesGeoJSON | null> => {
    try {
      const params = new URLSearchParams();
      if (voltageKv) params.set('voltage_kv', voltageKv.toString());
      if (lineType) params.set('line_type', lineType);
      if (maxRecords) params.set('max_records', maxRecords.toString());
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/transpower/transmission-lines/geojson${query ? `?${query}` : ''}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get sites (substations) data
  getSites: async (
    siteType?: 'ACSTN' | 'HVDC' | 'TEE',
    status?: string,
    maxRecords?: number
  ): Promise<SitesResponse | null> => {
    try {
      const params = new URLSearchParams();
      if (siteType) params.set('site_type', siteType);
      if (status) params.set('status', status);
      if (maxRecords) params.set('max_records', maxRecords.toString());
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/transpower/sites${query ? `?${query}` : ''}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get sites as GeoJSON for map visualization
  getSitesGeoJSON: async (
    siteType?: 'ACSTN' | 'HVDC' | 'TEE',
    status?: string,
    maxRecords?: number
  ): Promise<SitesGeoJSON | null> => {
    try {
      const params = new URLSearchParams();
      if (siteType) params.set('site_type', siteType);
      if (status) params.set('status', status);
      if (maxRecords) params.set('max_records', maxRecords.toString());
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/transpower/sites/geojson${query ? `?${query}` : ''}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get structures (towers/poles) data
  getStructures: async (
    longType?: string,
    status?: string,
    maxRecords?: number
  ): Promise<StructuresResponse | null> => {
    try {
      const params = new URLSearchParams();
      if (longType) params.set('long_type', longType);
      if (status) params.set('status', status);
      if (maxRecords) params.set('max_records', maxRecords.toString());
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/transpower/structures${query ? `?${query}` : ''}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get structures as GeoJSON
  getStructuresGeoJSON: async (
    longType?: string,
    status?: string,
    maxRecords?: number
  ): Promise<StructuresGeoJSON | null> => {
    try {
      const params = new URLSearchParams();
      if (longType) params.set('long_type', longType);
      if (status) params.set('status', status);
      if (maxRecords) params.set('max_records', maxRecords.toString());
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/transpower/structures/geojson${query ? `?${query}` : ''}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get spans (line segments) data
  getSpans: async (
    status?: string,
    maxRecords?: number
  ): Promise<SpansResponse | null> => {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (maxRecords) params.set('max_records', maxRecords.toString());
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/transpower/spans${query ? `?${query}` : ''}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get spans as GeoJSON
  getSpansGeoJSON: async (
    status?: string,
    maxRecords?: number
  ): Promise<SpansGeoJSON | null> => {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (maxRecords) params.set('max_records', maxRecords.toString());
      const query = params.toString();
      const response = await apiFetch(`/nz-grid/transpower/spans/geojson${query ? `?${query}` : ''}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get transmission corridors (buffer zones) as GeoJSON
  getCorridorsGeoJSON: async (maxRecords?: number): Promise<CorridorsGeoJSON | null> => {
    try {
      const query = maxRecords ? `?max_records=${maxRecords}` : '';
      const response = await apiFetch(`/nz-grid/transpower/corridors/geojson${query}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Get Cook Strait HVDC protection zone as GeoJSON
  getCookStraitZoneGeoJSON: async (): Promise<CookStraitZoneGeoJSON | null> => {
    try {
      const response = await apiFetch('/nz-grid/transpower/cook-strait-zone/geojson');
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },
};

// EM6 Live Data Types (ALL FREE - no authentication required)
export interface CarbonIntensityResponse {
  nz_carbon_tonnes: number | null;
  nz_carbon_gkwh: number | null;
  nz_carbon_change_gkwh: number | null;
  nz_renewable_pct: number | null;
  max_24hrs_gkwh: number | null;
  min_24hrs_gkwh: number | null;
  current_month_avg_gkwh: number | null;
  current_year_avg_gkwh: number | null;
  timestamp: string | null;
  provenance: Provenance;
}

export interface WindSolarForecastItem {
  trading_period: number;
  timestamp: string;
  region: string;
  generation_type: string;
  forecast_mw: number;
  potential_mw: number | null;
  cleared_mw: number | null;
  shortfall_mw: number | null;
}

export interface WindSolarForecastResponse {
  items: WindSolarForecastItem[];
  provenance: Provenance;
}

export interface RegionalPriceItem {
  region: string | null;
  grid_zone_id: number | null;
  price: number | null;
  trading_period: number | null;
  timestamp: string | null;
}

export interface RegionalPricesResponse {
  items: RegionalPriceItem[];
  provenance: Provenance;
}

export interface NodalPriceItem {
  node_id: string;
  node_name: string | null;
  price: number;
  trading_period: number | null;
  timestamp: string | null;
}

export interface NodalPrices24HrsResponse {
  items: NodalPriceItem[];
  nodes: string[];
  provenance: Provenance;
}

// Transpower ArcGIS Types (FREE - no authentication required)
export interface TransmissionLineItem {
  object_id: number;
  name: string;
  voltage_kv: number;
  status: string;
  line_type: string;
  description: string;
  length_km: number;
}

export interface TransmissionLinesResponse {
  items: TransmissionLineItem[];
  total: number;
  provenance: Provenance;
}

export interface TransmissionLineFeature {
  type: 'Feature';
  properties: {
    id: number;
    name: string;
    voltage_kv: number;
    status: string;
    line_type: string;
    description: string;
    length_km: number;
  };
  geometry: {
    type: 'MultiLineString';
    coordinates: number[][][];
  };
}

export interface TransmissionLinesGeoJSON {
  type: 'FeatureCollection';
  features: TransmissionLineFeature[];
  metadata: {
    source: string;
    count: number;
    voltage_filter: number | null;
    type_filter: string | null;
    provenance?: {
      tier: string;
      source: string;
      as_of: string;
    };
  };
}

// Sites (Substations)
export interface SiteItem {
  object_id: number;
  name: string;
  site_type: string;
  status: string;
  description: string;
  lat: number;
  lon: number;
}

export interface SitesResponse {
  items: SiteItem[];
  total: number;
  provenance: Provenance;
}

export interface SiteFeature {
  type: 'Feature';
  properties: {
    id: number;
    name: string;
    site_type: string;
    status: string;
    description: string;
  };
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
}

export interface SitesGeoJSON {
  type: 'FeatureCollection';
  features: SiteFeature[];
  metadata: {
    source: string;
    count: number;
    site_type_filter: string | null;
    status_filter: string | null;
    provenance?: { tier: string; source: string; as_of: string };
  };
}

// Structures (Towers/Poles)
export interface StructureItem {
  object_id: number;
  name: string;
  long_type: string;
  status: string;
  description: string;
  loc_type: string;
  x: number;
  y: number;
}

export interface StructuresResponse {
  items: StructureItem[];
  total: number;
  provenance: Provenance;
}

export interface StructuresGeoJSON {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: {
      id: number;
      name: string;
      long_type: string;
      status: string;
      description: string;
      loc_type: string;
    };
    geometry: { type: 'Point'; coordinates: [number, number] };
  }>;
  metadata: { source: string; count: number; provenance?: { tier: string; source: string; as_of: string } };
}

// Spans (Line Segments)
export interface SpanItem {
  object_id: number;
  name: string;
  status: string;
  description: string;
  loc_type: string;
  length_km: number;
}

export interface SpansResponse {
  items: SpanItem[];
  total: number;
  provenance: Provenance;
}

export interface SpansGeoJSON {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: { id: number; name: string; status: string; length_km: number };
    geometry: { type: 'MultiLineString'; coordinates: number[][][] };
  }>;
  metadata: { source: string; count: number; provenance?: { tier: string; source: string; as_of: string } };
}

// Corridors (Buffer Zones)
export interface CorridorsGeoJSON {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: { id: number };
    geometry: { type: 'Polygon'; coordinates: number[][][] };
  }>;
  metadata: { source: string; count: number; description: string; provenance?: { tier: string; source: string; as_of: string } };
}

// Cook Strait Protection Zone
export interface CookStraitZoneGeoJSON {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: { id: number; zone_id: number; length_km: number };
    geometry: { type: 'MultiLineString'; coordinates: number[][][] };
  }>;
  metadata: { source: string; count: number; description: string; provenance?: { tier: string; source: string; as_of: string } };
}

// =============================================================================
// Dashboard API Types
// =============================================================================

export type DataTier = 'LIVE' | 'MODELLED' | 'SCENARIO' | 'SYSTEM' | 'BACKUP';

export interface Provenance {
  tier: DataTier;
  source: string;
  as_of: string;
  generated_at: string;
  inputs: Record<string, any>;
  warnings: string[];
}

export interface GenerationMixItem {
  fuel_type: string;
  capacity_mw: number;
  percentage: number;
  color: string;
}

export interface IslandSummary {
  name: string;
  substations: number;
  generators: number;
  capacity_mw: number;
  load_mw: number;
  stress_level: number;
}

export interface GridMetrics {
  total_capacity_mw: number;
  total_load_mw: number;
  capacity_factor: number;
  generation_mix: GenerationMixItem[];
  islands: IslandSummary[];
  hvdc_flow_mw: number;
  hvdc_direction: 'north' | 'south';
}

export interface SolverMetrics {
  last_solve_time_ms: number;
  last_iterations: number;
  last_converged: boolean;
  avg_solve_time_ms: number;
  total_solves: number;
  success_rate: number;
}

export interface SystemHealth {
  api_uptime_seconds: number;
  memory_usage_mb: number;
  memory_percent: number;
  cpu_percent: number;
  active_connections: number;
  rust_solver_available: boolean;
}

export interface RegionalStress {
  region_code: string;
  region_name: string;
  lat: number;
  lon: number;
  stress_level: number;
  load_mw: number;
  capacity_mw: number;
}

export interface DashboardMetrics {
  timestamp: string;
  provenance: Provenance;
  grid: GridMetrics;
  solver: SolverMetrics;
  health: SystemHealth;
  regional_stress: RegionalStress[];
}

export const dashboardApi = {
  // Get comprehensive dashboard metrics
  getMetrics: async (loadFactor?: number): Promise<DashboardMetrics> => {
    const params = loadFactor ? `?load_factor=${loadFactor}` : '';
    const response = await apiFetch(`/dashboard/metrics${params}`);
    if (!response.ok) throw new Error('Failed to fetch dashboard metrics');
    return response.json();
  },

  // Get generation mix
  getGenerationMix: async (): Promise<{
    total_capacity_mw: number;
    mix: GenerationMixItem[];
  }> => {
    const response = await apiFetch('/dashboard/generation-mix');
    if (!response.ok) throw new Error('Failed to fetch generation mix');
    return response.json();
  },

  // Get island balance
  getIslandBalance: async (loadFactor?: number): Promise<{
    north_island: { generation_mw: number; load_mw: number; balance_mw: number };
    south_island: { generation_mw: number; load_mw: number; balance_mw: number };
    hvdc: { flow_mw: number; direction: string; capacity_mw: number; utilization_percent: number };
  }> => {
    const params = loadFactor ? `?load_factor=${loadFactor}` : '';
    const response = await apiFetch(`/dashboard/island-balance${params}`);
    if (!response.ok) throw new Error('Failed to fetch island balance');
    return response.json();
  },

  // Get solver performance
  getSolverPerformance: async (): Promise<{
    total_solves: number;
    successful_solves: number;
    failed_solves: number;
    success_rate_percent: number;
    total_time_ms: number;
    avg_time_ms: number;
    last_solve: { time_ms: number; iterations: number; converged: boolean };
  }> => {
    const response = await apiFetch('/dashboard/solver-performance');
    if (!response.ok) throw new Error('Failed to fetch solver performance');
    return response.json();
  },

  // Get system health
  getHealth: async (): Promise<{
    status: string;
    uptime_seconds: number;
    uptime_formatted: string;
    memory: { rss_mb: number; vms_mb: number; percent: number };
    cpu: { percent: number; num_threads: number };
    components: {
      rust_solver: { available: boolean; version: string | null };
      redis: { available: boolean };
      celery: { available: boolean };
    };
    timestamp: string;
  }> => {
    const response = await apiFetch('/dashboard/health');
    if (!response.ok) throw new Error('Failed to fetch health');
    return response.json();
  },
};

export interface DataKernelStatus {
  kafka_enabled: boolean;
  kafka_available: boolean;
  brokers: string[];
  telemetry_topic: string;
  breaker_status_topic: string;
  consumer_group_id: string;
}

export const dataKernelApi = {
  getStatus: async (): Promise<DataKernelStatus> => {
    const response = await apiFetch('/data-kernel/status');
    if (!response.ok) throw new Error('Failed to fetch data kernel status');
    return response.json();
  },
};

export interface DataCentreEstimate {
  ambient_temp_c: number;
  it_load_mw: number;
  pue: number;
  total_facility_mw: number;
  ups_available_mw: number;
  diesel_available_mw: number;
}

export const dataCentreApi = {
  estimate: async (payload: {
    ambient_temp_c: number;
    it_load_mw?: number | null;
  }): Promise<DataCentreEstimate> => {
    const response = await apiFetch('/datacentre/estimate', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to estimate data centre state');
    return response.json();
  },
};

export interface PriceDisparity {
  min_price: number | null;
  max_price: number | null;
  ratio: number | null;
  source: string | null;
}

export interface FairnessRunMetrics {
  run_id: string;
  unserved_energy_mwh: number;
  curtailment_mwh: number;
  congestion_hours: number;
  scarcity_hours: number;
  total_demand_mwh: number;
  data_centre_mwh?: number | null;
  data_centre_backup_mwh?: number | null;
}

export interface FairnessAggregate {
  unserved_energy_mwh: number | null;
  curtailment_mwh: number | null;
  congestion_hours: number | null;
  scarcity_hours: number | null;
  total_demand_mwh: number | null;
  data_centre_mwh?: number | null;
  data_centre_backup_mwh?: number | null;
}

export interface FairnessResponse {
  generated_at: string;
  runs: FairnessRunMetrics[];
  aggregate: FairnessAggregate;
  price_disparity?: PriceDisparity | null;
  warnings: string[];
}

export interface AttributionNode {
  id: string;
  kind?: string;
  type?: string;
}

export interface AttributionEdge {
  source: string;
  target: string;
  weight: number;
}

export interface AttributionResponse {
  version?: string;
  run_id?: string;
  base_harm?: number;
  harm_metric?: string;
  method?: string;
  nodes?: AttributionNode[];
  edges?: AttributionEdge[];
  shapley?: any;
}

export const resultsApi = {
  getFairness: async (runId?: string): Promise<FairnessResponse> => {
    const params = new URLSearchParams();
    if (runId) params.set('run_id', runId);
    const query = params.toString();
    const response = await apiFetch(`/results/fairness${query ? `?${query}` : ''}`);
    if (!response.ok) throw new Error('Failed to fetch fairness metrics');
    return response.json();
  },
};

export interface StrategyListItem {
  name: string;
  description?: string | null;
}

export interface StrategyResponse {
  name: string;
  description?: string | null;
  payload: Record<string, any>;
}

export const strategiesApi = {
  list: async (): Promise<StrategyListItem[]> => {
    const response = await apiFetch('/strategies');
    if (!response.ok) throw new Error('Failed to load strategies');
    return response.json();
  },
  get: async (name: string): Promise<StrategyResponse> => {
    const response = await apiFetch(`/strategies/${name}`);
    if (!response.ok) throw new Error('Failed to load strategy');
    return response.json();
  },
};

// Export singleton client as well
export const apiClient = new ApiClient();
export default api;
